/**
 * Browser & device support matrix — the single source of truth (ADR-0062).
 *
 * The PUBLIC statement is binary: an environment is `supported` or not
 * (owner decision 2026-08-06). The in-bundle classifier distinguishes
 * two classes inside "not supported": `unsupported-capable` (technically
 * able to run the product → one-time notice, call proceeds, no support
 * commitment) and `blocked` (cannot run it → the pre-call gate refuses the
 * live call with guidance). The app-level classifier and notice are live;
 * the call-specific API/webview gate remains follow-up work. Anything user-
 * or customer-facing renders the binary statement, never the internal class
 * names.
 *
 * Every other copy of these numbers and policy decisions (browserslist,
 * the web + manager + admin Vite targets, customer index.html boot shims, the
 * generated functional doc, and the in-bundle classifier) derives
 * from this module or is asserted equal to it by tests — change values HERE
 * and bump SUPPORT_MATRIX_VERSION, then let the sync tests walk you through
 * the remaining edits (ADR-0062 D2/D3, Verification).
 */
/** Bumped on ANY change to the review date, floors, policy, or probe below. */
export const SUPPORT_MATRIX_VERSION = '1.13.0';
/**
 * Stable namespace for app-owned FAQ entries. During the transitional
 * frontend bootstrap these entries are composed directly from policy; after
 * the backend FAQ cutover, code syncs their generated content only into an
 * In-review revision and an admin publishes it manually. Editorial FAQ
 * storage must reject these keys in either phase so a record cannot shadow
 * policy-backed recovery guidance (ADR-0062 D4).
 */
export const BROWSER_SUPPORT_FAQ_KEY = 'browser-support';
export const SYSTEM_MANAGED_FAQ_KEYS = [BROWSER_SUPPORT_FAQ_KEY];
export function isSystemManagedFaqKey(value) {
    return SYSTEM_MANAGED_FAQ_KEYS.some((key) => key === value);
}
export const SUPPORT_MATRIX = {
    /** Bumped whenever the matrix is re-reviewed (phase boundaries, vendor moves). */
    reviewedAt: '2026-09-10',
    /**
     * Engine floors for combinations listed as supported or as intended
     * support targets. Meeting a floor alone does not create a public support
     * promise; the platform must also be present in
     * `policy.supportedPlatformsByBrowser`. macOS Safari has a 16.5 support
     * floor above its 16.4 parse boundary. iOS/iPadOS retain a separate 16.4
     * target; on iOS every browser is WebKit, so the iOS entry gates on OS
     * version, never browser brand.
     */
    supportedFrom: {
        chrome: 111,
        edge: 111,
        safari: '16.5',
        ios: '16.4',
    },
    /**
     * Platform-specific public support floors approved from the recorded
     * release evidence. Android uses one Chrome engine/runtime floor: Chrome
     * 119 passed the full phone journey, while independent tablet runs proved
     * the tablet layout, touch, lifecycle, Voice, Avatar, and Manager surfaces.
     * ADR-0062 records this explicit combined-evidence decision (D1/D6).
     */
    supportedFromByPlatform: {
        'android-phone': 119,
        'android-tablet': 119,
    },
    /**
     * The guarantee boundary of the Tailwind CSS v4 runtime floor. Tailwind v4
     * relies on modern CSS including color-mix() in the oklab color space and
     * registered custom properties. Below this the stylesheet is not promised
     * to render correctly → blocked. Meeting the floor without matching the
     * public platform policy remains unsupported-capable.
     */
    blockedBelow: {
        chrome: 111,
        edge: 111,
        firefox: 128,
        safari: '16.4',
        ios: '16.4',
    },
    /**
     * Runtime APIs the future pre-call probes check (detection card). WebSocket and
     * EventSource predate every entry and need no probe. Probes override UA
     * classification pessimistically for the live call only.
     */
    requiredApis: [
        'navigator.mediaDevices',
        'RTCPeerConnection',
        'AudioWorkletNode',
        'WebAssembly',
    ],
    /**
     * Classification policy consumed by documentation, the live app classifier,
     * and the future call-specific gate. Browser/OS/device detection remains
     * owned by apps/web/src/telemetry/device-environment.ts (ADR-0057);
     * consumers apply this policy to that shared detector result and must not
     * parse UA again.
     */
    policy: {
        /** Customer products covered by the one client-facing support statement. */
        customerApps: ['web', 'manager'],
        /** Exact browser/platform combinations that are publicly `supported`. */
        supportedPlatformsByBrowser: {
            chrome: ['windows', 'macos', 'android-phone', 'android-tablet'],
            edge: ['windows', 'macos'],
            safari: ['macos'],
            iosWebKit: ['ios-phone', 'ipados-tablet'],
        },
        /**
         * Intended support targets that are implemented but cannot move into the
         * public supported set until the corresponding release evidence below is
         * recorded. Keeping these separate prevents an unverified target from
         * becoming a live support promise.
         */
        verificationPendingPlatformsByBrowser: {
            desktop: {
                chrome: [],
                edge: [],
                safari: [],
                iosWebKit: [],
            },
            mobile: {
                chrome: [],
                edge: [],
                safari: [],
                iosWebKit: [],
            },
        },
        releaseEvidence: {
            desktopBrowserCoverage: {
                status: 'verified',
                requiredChecks: [
                    'current-chrome',
                    'chrome-111',
                    'current-edge',
                    'edge-111',
                    'current-macos-safari',
                    'macos-safari-16.5',
                ],
            },
            physicalMobileCall: {
                status: 'verified',
                verifiedPlatforms: [
                    'android-phone',
                    'android-tablet',
                    'ios-phone',
                    'ipados-tablet',
                ],
                pendingPlatforms: [],
                requiredChecks: [
                    'microphone-permission',
                    'audio-routing',
                    'background-foreground-recovery',
                    'call-end',
                    'manager-roster',
                ],
            },
        },
        /**
         * Localized labels and messages used to generate the pre-bundle recovery
         * screen. Browser/platform combinations come from the two maps above;
         * BOOT_SHIM_SOURCE contains no independent copy of those combinations.
         */
        recoveryCopy: {
            browserOrder: ['chrome', 'edge', 'safari', 'iosWebKit'],
            browserLabels: {
                en: {
                    chrome: 'Chrome',
                    edge: 'Edge',
                    safari: 'Safari',
                    iosWebKit: 'iOS/iPadOS',
                },
                ja: {
                    chrome: 'Chrome',
                    edge: 'Edge',
                    safari: 'Safari',
                    iosWebKit: 'iOS／iPadOS',
                },
            },
            platformLabels: {
                en: {
                    windows: 'Windows',
                    macos: 'macOS',
                    'android-phone': 'Android phone',
                    'android-tablet': 'Android tablet',
                    'ios-phone': 'iPhone standalone browser',
                    'ipados-tablet': 'iPad standalone browser',
                },
                ja: {
                    windows: 'Windows',
                    macos: 'macOS',
                    'android-phone': 'Androidスマートフォン',
                    'android-tablet': 'Androidタブレット',
                    'ios-phone': 'iPhoneの通常のブラウザ',
                    'ipados-tablet': 'iPadの通常のブラウザ',
                },
            },
            localeText: {
                en: {
                    faqQuestion: 'Which browsers and devices are supported?',
                    faqRowLabels: {
                        supported: 'Currently supported',
                        desktopPending: 'Pending desktop release verification',
                        mobilePending: 'Pending physical mobile verification',
                    },
                    title: 'This browser is not supported',
                    supportedIntro: 'Please open Speaking Coach in one of these currently supported environments:',
                    noSupportedIntro: 'No browser/device environment is publicly supported until the required release verification is complete.',
                    desktopPendingIntro: 'Desktop support targets are pending current and at-floor release verification and are not yet supported:',
                    mobilePendingIntro: 'These mobile targets still require physical-device learner-call and Manager-roster verification and are not yet supported:',
                    technicalFloor: 'Technical run floor (not a support promise): Chrome/Edge 111+, Firefox 128+, and Safari/iOS/iPadOS 16.4+. Older engines are blocked because they lack required CSS features.',
                    exclusions: 'Firefox, Chromium-based browsers other than Chrome and Edge, and in-app browsers are not supported.',
                    floorSuffix: '+',
                    listSeparator: ', ',
                },
                ja: {
                    faqQuestion: 'どのブラウザと端末がサポートされていますか？',
                    faqRowLabels: {
                        supported: '現在のサポート対象',
                        desktopPending: 'デスクトップのリリース検証待ち',
                        mobilePending: 'モバイル実機での検証待ち',
                    },
                    title: 'このブラウザはサポートされていません',
                    supportedIntro: 'Speaking Coachは、現在サポートされている以下の環境でご利用ください。',
                    noSupportedIntro: '必要なリリース検証が完了するまで、正式なサポート対象環境はありません。',
                    desktopPendingIntro: 'デスクトップ対応は、現行版および最低対応版でのリリース検証が完了していないため、現時点ではサポート対象外です。',
                    mobilePendingIntro: '以下のモバイル環境は、学習者の通話とHR向け名簿の実機検証が必要なため、現時点ではサポート対象外です。',
                    technicalFloor: 'サポートを保証するものではない技術的な動作下限はChrome／Edge 111以降、Firefox 128以降、Safari／iOS／iPadOS 16.4以降です。これより古いブラウザは必要なCSS機能を満たさないためブロックされます。',
                    exclusions: 'Firefox、ChromeとEdge以外のChromium系ブラウザ、およびアプリ内ブラウザはサポート対象外です。',
                    floorSuffix: '以降',
                    listSeparator: '、',
                },
            },
        },
        /** Browser-band defaults before platform/device/API overrides. */
        browserBands: {
            belowFloor: 'blocked',
            firefoxAtOrAboveFloor: 'unsupported-capable',
            otherChromiumAtOrAboveFloor: 'unsupported-capable',
        },
        /** A listed engine on any unlisted platform can never become supported. */
        unlistedPlatform: 'unsupported-capable',
        /** The stricter browser/platform/device/API result always wins. */
        combinationRule: 'strictest',
        /** Surface-specific overrides applied after the browser floor. */
        overrides: {
            phone: {
                app: 'supported',
                liveCall: 'supported',
            },
            ieMode: {
                app: 'blocked',
                liveCall: 'blocked',
            },
            webview: {
                app: 'unsupported-capable',
                liveCall: 'blocked',
            },
            unknownEnvironmentWithRequiredApis: {
                app: 'unsupported-capable',
                liveCall: 'unsupported-capable',
            },
            missingRequiredApi: {
                app: 'unsupported-capable',
                liveCall: 'blocked',
            },
        },
    },
};
/**
 * The ES2022 syntax probe the boot shim feeds `new Function(...)`. It remains
 * a version-free guard against a bundle parse failure, while BOOT_CSS_PROBE
 * below sets the stricter Tailwind v4 runtime floor.
 */
export const BOOT_SYNTAX_PROBE = 'class __CoachSupportProbe{static{}}';
/**
 * Version-free CSS capabilities required by Tailwind CSS v4. The boot shim
 * checks both the oklab color-mix syntax and registered custom properties
 * before loading the application bundle.
 */
export const BOOT_CSS_PROBE = {
    property: 'color',
    value: 'color-mix(in oklab, red, red)',
    registrationApi: 'registerProperty',
};
/**
 * Durable marker written by the ES5 shim and consumed by the module
 * bootstrap. The marker is necessary because `build.target` permits the
 * binding syntax but does not force it into every bundle: a below-floor
 * browser can reject the probe yet still parse a particular build.
 */
export const BOOT_BLOCK_ATTRIBUTE = 'data-coach-unsupported-browser';
/** Return the public support floor for one eligible browser/platform row. */
export function supportFloorForPlatform(browser, platform) {
    const platformFloors = SUPPORT_MATRIX.supportedFromByPlatform;
    const platformFloor = platformFloors[platform];
    if (platformFloor !== undefined) {
        return platformFloor;
    }
    switch (browser) {
        case 'chrome':
            return SUPPORT_MATRIX.supportedFrom.chrome;
        case 'edge':
            return SUPPORT_MATRIX.supportedFrom.edge;
        case 'safari':
            return SUPPORT_MATRIX.supportedFrom.safari;
        case 'iosWebKit':
            return SUPPORT_MATRIX.supportedFrom.ios;
    }
}
function supportRows(platformsByBrowser, locale) {
    const copy = SUPPORT_MATRIX.policy.recoveryCopy;
    return copy.browserOrder.flatMap((browser) => {
        const platforms = platformsByBrowser[browser];
        const platformsByFloor = new Map();
        for (const platform of platforms) {
            const floor = supportFloorForPlatform(browser, platform);
            const peers = platformsByFloor.get(floor) ?? [];
            peers.push(platform);
            platformsByFloor.set(floor, peers);
        }
        return [...platformsByFloor].map(([floor, peers]) => ({
            browser: copy.browserLabels[locale][browser],
            platforms: peers
                .map((platform) => copy.platformLabels[locale][platform])
                .join(copy.localeText[locale].listSeparator),
            minimum: `${floor}${copy.localeText[locale].floorSuffix}`,
        }));
    });
}
/** The publicly supported combinations as table rows (the start guide). */
export function supportedBrowserRows(locale) {
    return supportRows(SUPPORT_MATRIX.policy.supportedPlatformsByBrowser, locale);
}
function recoveryRows(platformsByBrowser, locale) {
    return supportRows(platformsByBrowser, locale).map((row) => `${row.browser} ${row.minimum}: ${row.platforms}`);
}
function renderRecoveryRows(platformsByBrowser, locale) {
    return recoveryRows(platformsByBrowser, locale)
        .map((row) => `<li>${row}</li>`)
        .join('');
}
/**
 * Render the customer FAQ from the same structured rows as the pre-bundle
 * recovery screen. Localized prose supplies presentation only; browser
 * floors, platforms, evidence bands, and exclusions all come from the
 * versioned matrix above. The workflow fields describe the post-cutover
 * backend contract; the transitional frontend renders this content directly
 * until that migration completes (ADR-0062 D4).
 */
export function supportFaqContent(locale) {
    const policy = SUPPORT_MATRIX.policy;
    const copy = policy.recoveryCopy.localeText[locale];
    const labels = copy.faqRowLabels;
    const supported = recoveryRows(policy.supportedPlatformsByBrowser, locale);
    const desktopPending = recoveryRows(policy.verificationPendingPlatformsByBrowser.desktop, locale);
    const mobilePending = recoveryRows(policy.verificationPendingPlatformsByBrowser.mobile, locale);
    return {
        key: BROWSER_SUPPORT_FAQ_KEY,
        source: 'system',
        editable: false,
        automatedStatusCeiling: 'in-review',
        publishAction: 'manual-admin',
        version: SUPPORT_MATRIX_VERSION,
        reviewedAt: SUPPORT_MATRIX.reviewedAt,
        question: copy.faqQuestion,
        intro: supported.length > 0 ? copy.supportedIntro : copy.noSupportedIntro,
        bullets: [
            ...supported.map((row) => `${labels.supported}: ${row}`),
            ...desktopPending.map((row) => `${labels.desktopPending}: ${row}`),
            ...mobilePending.map((row) => `${labels.mobilePending}: ${row}`),
            copy.exclusions,
        ],
    };
}
function renderBootRecoveryHtml() {
    const policy = SUPPORT_MATRIX.policy;
    const copy = policy.recoveryCopy;
    const japanese = copy.localeText.ja;
    const english = copy.localeText.en;
    const listStyle = 'padding-left:1.25rem;margin:.5rem 0';
    const noteStyle = 'font-size:.9rem';
    const supportedRowsJa = renderRecoveryRows(policy.supportedPlatformsByBrowser, 'ja');
    const supportedRowsEn = renderRecoveryRows(policy.supportedPlatformsByBrowser, 'en');
    const desktopPendingRowsJa = renderRecoveryRows(policy.verificationPendingPlatformsByBrowser.desktop, 'ja');
    const desktopPendingRowsEn = renderRecoveryRows(policy.verificationPendingPlatformsByBrowser.desktop, 'en');
    const mobilePendingRowsJa = renderRecoveryRows(policy.verificationPendingPlatformsByBrowser.mobile, 'ja');
    const mobilePendingRowsEn = renderRecoveryRows(policy.verificationPendingPlatformsByBrowser.mobile, 'en');
    return ('<div style="font-family:sans-serif;max-width:32rem;margin:5vh auto 2rem;padding:0 1rem;line-height:1.6">' +
        `<h1 style="font-size:1.25rem">${japanese.title}</h1>` +
        (supportedRowsJa
            ? `<p>${japanese.supportedIntro}</p><ul style="${listStyle}">${supportedRowsJa}</ul>`
            : `<p>${japanese.noSupportedIntro}</p>`) +
        (desktopPendingRowsJa
            ? `<p style="${noteStyle}">${japanese.desktopPendingIntro}</p><ul style="${listStyle}">${desktopPendingRowsJa}</ul>`
            : '') +
        (mobilePendingRowsJa
            ? `<p style="${noteStyle}">${japanese.mobilePendingIntro}</p><ul style="${listStyle}">${mobilePendingRowsJa}</ul>`
            : '') +
        `<p style="${noteStyle}">${japanese.technicalFloor}</p>` +
        `<p style="${noteStyle}">${japanese.exclusions}</p>` +
        '<hr style="margin:1rem 0;border:none;border-top:1px solid #ccc">' +
        `<h2 style="font-size:1.1rem">${english.title}</h2>` +
        (supportedRowsEn
            ? `<p>${english.supportedIntro}</p><ul style="${listStyle}">${supportedRowsEn}</ul>`
            : `<p>${english.noSupportedIntro}</p>`) +
        (desktopPendingRowsEn
            ? `<p style="${noteStyle}">${english.desktopPendingIntro}</p><ul style="${listStyle}">${desktopPendingRowsEn}</ul>`
            : '') +
        (mobilePendingRowsEn
            ? `<p style="${noteStyle}">${english.mobilePendingIntro}</p><ul style="${listStyle}">${mobilePendingRowsEn}</ul>`
            : '') +
        `<p style="${noteStyle}">${english.technicalFloor}</p>` +
        `<p style="${noteStyle}">${english.exclusions}</p>` +
        '</div>');
}
/** Exact recovery markup generated from the versioned policy above. */
export const BOOT_RECOVERY_HTML = renderBootRecoveryHtml();
/**
 * The pre-bundle boot shim, inlined verbatim into apps/web/index.html
 * (byte-checked by apps/web/test/support-matrix-sync.test.ts — edit HERE,
 * re-paste there, never the other way round). Plain ES5: this recovery code
 * runs even in an IE-mode tab and before the module bootstrap in Chrome 90
 * or Safari 15.
 *
 * Failure semantics (ADR-0062 D4): fail closed when the required CSS
 * capabilities are absent or the syntax probe throws SyntaxError. Any other
 * syntax-probe exception — notably the EvalError a CSP without 'unsafe-eval'
 * throws on every browser — says nothing about the engine and does not alter
 * the independently evaluated CSS result. Call-path API availability remains
 * surface-dependent and belongs to the in-bundle gate.
 */
// Japanese copy inside BOOT_SHIM_SOURCE below is locale-neutral authored policy.
// One template literal, so edit-and-repaste into index.html stays a copy
// job with no quote-escaping hazards. The syntax probe, durable marker, and
// CSS capability probe, policy-rendered recovery HTML are the only
// interpolations; the emitted
// string must stay plain ES5 (the acorn ecmaVersion:5 parse test enforces this).
export const BOOT_SHIM_SOURCE = `(function () {
  var blocked = typeof CSS === "undefined" ||
    typeof CSS.supports !== "function" ||
    !CSS.supports("${BOOT_CSS_PROBE.property}", "${BOOT_CSS_PROBE.value}") ||
    typeof CSS.${BOOT_CSS_PROBE.registrationApi} !== "function";
  try {
    new Function("${BOOT_SYNTAX_PROBE}");
  } catch (e) {
    if (e && e.name === "SyntaxError") blocked = true;
  }
  if (!blocked) return;
  var root = document.getElementById("root");
  if (!root) return;
  root.setAttribute("${BOOT_BLOCK_ATTRIBUTE}", "true");
  root.innerHTML = ${JSON.stringify(BOOT_RECOVERY_HTML)};
})();`;
/**
 * The browserslist entries apps/web/package.json must carry (capable-and-up
 * floors). Android Chrome tracks desktop
 * Chrome, so `and_chr` is deliberately waived (ADR-0062 D3).
 */
export function browserslistFromMatrix() {
    const f = SUPPORT_MATRIX.blockedBelow;
    return [
        `chrome >= ${f.chrome}`,
        `edge >= ${f.edge}`,
        `firefox >= ${f.firefox}`,
        `safari >= ${f.safari}`,
        `ios_saf >= ${f.ios}`,
    ];
}
/**
 * The esbuild targets both SPA Vite configs must carry — their bundles'
 * syntax is guaranteed parseable down to the shared blocked floor.
 * esbuild tracks `ios` separately from `safari`; identical today, pinned
 * so it stays that way.
 */
export function viteTargetsFromMatrix() {
    const f = SUPPORT_MATRIX.blockedBelow;
    return [
        `chrome${f.chrome}`,
        `edge${f.edge}`,
        `firefox${f.firefox}`,
        `safari${f.safari}`,
        `ios${f.ios}`,
    ];
}
//# sourceMappingURL=support-matrix.js.map